"use client"

import { useLocalStorage } from "@opensea/ui-kit/hooks"
import type { WalletName } from "@solana/wallet-adapter-base"
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react"
import { omit } from "es-toolkit/object"
import { useCallback, useEffect, useMemo } from "react"
import type { Address } from "../../types"
import type { Connection } from "./useConnections"
import { useConnectors } from "./useConnectors"

export function useSolanaConnections() {
  const { getConnectorStrict, getConnector } = useConnectors()
  const { wallets: solanaWallets, connecting, connected } = useSolanaWallet()

  const [walletAddressMap, setWalletAddressMap] = useLocalStorage<
    Record<WalletName, Address | undefined>
  >("opensea-wallet-solana-address-map", {})

  useEffect(() => {
    const newAddressMap = { ...walletAddressMap }
    solanaWallets.forEach(wallet => {
      if (wallet.adapter.publicKey) {
        newAddressMap[wallet.adapter.name] = wallet.adapter.publicKey.toBase58()
      }
    })
    setWalletAddressMap(newAddressMap)
  }, [solanaWallets, connecting, connected])

  const connections: Connection[] = useMemo(() => {
    return solanaWallets
      .filter(
        wallet =>
          wallet.adapter.connected || walletAddressMap[wallet.adapter.name],
      )
      .filter(wallet => getConnector(wallet.adapter.name))
      .map(wallet => ({
        connector: getConnectorStrict(wallet.adapter.name),
        chainId: 1,
        addresses: [
          wallet.adapter.publicKey?.toBase58() ??
            walletAddressMap[wallet.adapter.name] ??
            "",
        ],
        chainArch: "SVM" as const,
      }))
  }, [
    getConnectorStrict,
    solanaWallets,
    walletAddressMap,
    connecting,
    connected,
  ])

  const resetConnection = useCallback(
    (connectorId?: WalletName) => {
      if (!connectorId) {
        setWalletAddressMap({})
        return
      }
      setWalletAddressMap(prevAddressMap => omit(prevAddressMap, [connectorId]))
    },
    [setWalletAddressMap],
  )

  return { connections, resetConnection }
}
